import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== Master Butler (EN) full modernization — Implementer B scope =====
// Design canon: src/pages/about/overview/page.tsx & home (navy #1a2942 / brass #a38e65 / gold #d7b779 / BTN-08 / no ornamental dividers).
// Factual canon (no fabrication, prices unchanged, EN = no Japanese characters):
//   [S-cur] prior JA src/pages/qualifications/master-butler/page.tsx (15 chapters, ¥128,000, exam spec)
//   [S-overview] src/pages/about/overview/page.tsx (BTN08, section grammar)

const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

const curriculum = [
  { n: '01', t: 'Reflection on Professional Butler', d: 'Take stock of prior learning and rise to the vantage point a Master Butler requires. Shift from practitioner to manager and mentor.' },
  { n: '02', t: 'Butler Leadership Theory', d: 'The essence of leadership—team management, developing and guiding subordinates, and motivation—learning the power to lead people.' },
  { n: '03', t: 'Household Management', d: 'Running a large estate: staffing and management, the basics of budget control, and coordination with external contractors to oversee the whole household.' },
  { n: '04', t: 'Advanced VIP Service', d: 'Foundations of close protection, security awareness, emergency-response protocols, and crisis management—the highest level of VIP service.' },
  { n: '05', t: 'Applied International Protocol', d: 'Details of diplomatic etiquette, conduct at international events, cultural consideration in practice, and the basics of multilingual response for global settings.' },
  { n: '06', t: 'Techniques of Education and Instruction', d: 'Effective teaching methods, on-the-job training, feedback technique, and evaluation with career support—the art of developing people.' },
  { n: '07', t: 'Quality Management of Butler Service', d: 'Defining service quality, balancing standardisation with individual response, mechanisms for continuous improvement, and measuring client satisfaction.' },
  { n: '08', t: 'Advanced Problem-Solving', d: 'Responding to complex situations, judging priorities, taking multiple perspectives, and strategic thinking to cultivate high-level problem-solving.' },
  { n: '09', t: 'Business Knowledge for Butler Services', d: 'Business models for butler services, the basics of income-and-expenditure management, contracts and legal knowledge, and marketing thinking.' },
  { n: '10', t: 'Butler Duties at Luxury Hotels and Facilities', d: 'The role of the hotel butler, facility-specific operations, cooperation with other departments, and maintaining brand value within an organisation.' },
  { n: '11', t: 'Practice as a Private Butler', d: 'Duties at private residences, responses attuned to the family, building long-term trust, and the strict observance of confidentiality.' },
  { n: '12', t: 'Handling Special Situations', d: 'Coordination with medical and nursing care, responses to special needs, the care of cultural properties and artworks, and pet care.' },
  { n: '13', t: 'Butler Ethics and Philosophy (Advanced)', d: 'Professional pride, responses to ethical dilemmas, the social responsibility of the butler, and a stance of lifelong learning.' },
  { n: '14', t: 'Cultivating an International Outlook', d: 'Understanding overseas butler cultures and service trends, learning global standards, serving guests of diverse backgrounds, and the importance of information-gathering.' },
  { n: '15', t: 'Master Butler Comprehensive Exercise', d: 'Advanced case studies, management simulations, instruction practice, and a final comprehensive exam that integrates all your learning.' },
];

const recommended = [
  'Those who hold the Professional Butler certification',
  'Those aiming to lead a team of butlers',
  'Those wishing to educate and mentor other butlers',
  'Those aiming to manage large estates or hotels',
  'Those interested in running a butler-service business',
  'Those wishing to reach the very pinnacle of the profession',
  'Those wishing to work on an international stage',
];

const materials = [
  { t: 'Textbook (approx. 300 pages)', d: 'The most comprehensive dedicated textbook, covering everything from management theory to practice—the knowledge required at the summit of the profession.' },
  { t: 'Online Video Lessons', d: 'Advanced content including management cases—leadership in practice and instruction technique that only video can convey. One-year viewing period.' },
  { t: 'Practice Kit', d: 'The finest practice materials, including resources needed for management exercises.' },
  { t: 'Certification Exam & Answer Sheet', d: 'The most advanced exam, including case studies and essay questions worthy of a Master Butler.' },
];

const examRows = [
  ['Format', 'Written exam (essay, case study, discourse)'],
  ['Passing Score', '80 points or higher'],
  ['Deadline', 'Submit within 1 year of enrollment'],
  ['Eligibility', 'Professional Butler holders only'],
  ['Location', 'At home (distance exam)'],
  ['Retake', 'Unlimited retakes with a ¥15,000 retake fee'],
];

const ladder = [
  { key: 'associate', roman: 'I', name: 'Associate Butler', level: 'Beginner', price: '48,000', href: '/en/qualifications/associate-butler' },
  { key: 'professional', roman: 'II', name: 'Professional Butler', level: 'Intermediate', price: '78,000', href: '/en/qualifications/professional-butler' },
  { key: 'master', roman: 'III', name: 'Master Butler', level: 'Advanced', price: '128,000', href: '/en/qualifications/master-butler' },
];

function SecHead({ roman, en, children }: { roman: string; en: string; children: React.ReactNode }) {
  return (
    <div className="mb-9">
      <div className="-mx-5 sm:-mx-8 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 py-[18px]">
        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">{roman}. {en}</span>
      </div>
      <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.45] tracking-[0.01em]">{children}</h2>
    </div>
  );
}

export default function EnMasterButlerPage() {
  useEffect(() => {
    updateSEO({
      title: 'Master Butler Certification (Advanced) | Japan Butler Association',
      description: 'The Master Butler certification is the pinnacle of butler excellence—proving exceptional skill, deep knowledge, and the leadership to mentor and oversee. Master household management, crisis management, and instruction across 15 chapters. ¥128,000 for Professional Butler holders.',
      keywords: 'master butler, butler certification, advanced, household management, leadership, Japan Butler Association',
      canonical: 'https://butler-association.jp/en/qualifications/master-butler',
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Certifications', href: '/en/qualifications' },
    { label: 'Master Butler' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-fs-en-028-qualifications_master-butler.webp"
              alt="Stacked leather notebooks on a wooden table for advanced butler study"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_48%,rgba(16,20,26,0.36)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-5 sm:px-8 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Master Butler · Level III</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="font-['Playfair_Display',serif] font-medium text-white text-[clamp(30px,3.6vw,46px)] leading-[1.25] tracking-[0.01em] mb-3">
              To the summit, <span className="text-[#d7b779]">Master Butler</span>
            </h1>
            <p className="font-['Playfair_Display',serif] italic text-[#e7e2d8] text-sm tracking-[0.08em] mb-6">Advanced Level</p>
            <div className="space-y-5 mb-8">
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.9]">
                The highest certification in the butler profession. It proves exceptional skill and knowledge, together with the ability to mentor other butlers and manage complex situations.
              </p>
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.9]">
                From household management and crisis response to international protocol and instruction, you acquire everything a leader and overseer requires.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[10px] text-[#d7b779] mb-1">Fee</span>
                <span className="font-['Playfair_Display',serif] font-medium text-white text-[28px] leading-none">&yen;128,000</span>
                <span className="block font-['Playfair_Display',serif] text-[11px] text-white/60 mt-1">Tax &amp; shipping included</span>
              </div>
              <a href="/en/contact" className={BTN08_ON_NAVY}>Enroll Now</a>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb: placed right after the billboard hero (Yuichi 2026-07-17 note 2). Override shared Breadcrumb's header-clearance top padding via child selector */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 sm:py-18 lg:py-20 space-y-16 sm:space-y-20">

          <section>
            <SecHead roman="I" en="About This Certification">The power to lead <span className="text-[#a38e65]">and oversee</span></SecHead>
            <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl">
              <div className="space-y-5">
                <p>The Master Butler certification proves the highest level of butler knowledge and skill, together with leadership and management ability.</p>
                <p>Beyond practical skills, you acquire the capacity to lead a team, develop other butlers, and manage the service quality of an entire organisation.</p>
              </div>
              <p>The curriculum spans household management (running a large estate), advanced VIP service and crisis management, a deep understanding of international protocol, techniques of education and instruction, and the quality management of butler service—together with business knowledge and a global understanding of the industry.</p>
              <p>Building on the practical ability cultivated as a Professional, it develops leaders with a more strategic and comprehensive perspective. On completion, a career as a mentor and manager opens before you.</p>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              {[['Fee', '¥128,000'], ['Textbook', '~300 pp.'], ['Study', '6–8 mo.'], ['Prerequisite', 'Professional']].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-['Playfair_Display',serif] uppercase tracking-[0.08em] text-[11px] text-[#8f7a56] mb-1.5">{k}</dt>
                  <dd className="font-['Playfair_Display',serif] font-medium text-2xl text-[#1a2942]">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <SecHead roman="II" en="Why Master">Three roles <span className="text-[#a38e65]">of the highest tier</span></SecHead>
            <div className="grid md:grid-cols-3 gap-x-10 gap-y-8">
              {[
                { no: '01', ja: 'The Pinnacle', d: 'The highest recognition in the profession—proving outstanding expertise and trustworthiness in a tangible form.' },
                { no: '02', ja: 'Management', d: 'Overseeing a large estate, managing a team, and maintaining quality—cultivating the power to lead the service of an entire organisation.' },
                { no: '03', ja: 'Mentoring', d: 'Teaching and guiding other butlers, developing the next generation with the vision to lead the industry forward.' },
              ].map((f) => (
                <div key={f.no} className="border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none block mb-3">{f.no}</span>
                  <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl mb-2.5 leading-[1.4]">{f.ja}</h3>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <SecHead roman="III" en="Curriculum · 15 Chapters">Fifteen chapters of <span className="text-[#a38e65]">the art of oversight</span></SecHead>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl mb-8">
              The Master Butler certification explores the 15 areas a butler leader requires in depth—from practical skill to management and business knowledge—the most comprehensive content in the entire certification system.
            </p>
            <img
              src="/images/generated/jba-fs-en-029-qualifications_master-butler.webp"
              alt="Master Butler study materials on management and instruction"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.n} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.n}</span>
                  <div>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg mb-2.5 leading-[1.4]">{c.t}</h3>
                    <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <SecHead roman="IV" en="Recommended For">This certification is <span className="text-[#a38e65]">ideal for</span></SecHead>
            <ul className="grid sm:grid-cols-2 gap-x-10 border-t-2 border-[#1a2942]">
              {recommended.map((r) => (
                <li key={r} className="flex items-start gap-3 border-b border-[#e2ddd2] py-4">
                  <span className="text-[#a38e65] text-[13px] leading-none pt-1.5" aria-hidden="true">◆</span>
                  <span className="font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.7]">{r}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <SecHead roman="V" en="Course Materials">Everything you need, <span className="text-[#a38e65]">at its finest</span></SecHead>
            <div className="grid md:grid-cols-2 gap-6">
              {materials.map((m, i) => (
                <div key={m.t} className="flex gap-5 bg-[#f8f6f0] border border-[#a38e65]/25 p-6">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl leading-none shrink-0 pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-base sm:text-lg mb-2 leading-[1.4]">{m.t}</h3>
                    <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{m.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-[#1a2942] px-7 sm:px-10 py-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-7">
              <span className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm tracking-[0.06em] shrink-0">Special Bonus</span>
              <div>
                <h3 className="font-['Playfair_Display',serif] font-medium text-[#f8f6f0] text-lg mb-1">3-Hour Butler Salon Experience Ticket</h3>
                <p className="font-['Playfair_Display',serif] text-sm text-[#ded8cc] leading-[1.85]">A special ticket to experience real butler service at our Odaiba salon (¥30,000 value), deepening your Master-level learning.</p>
              </div>
            </div>
          </section>

          <section>
            <SecHead roman="VI" en="Certification Exam">The exam is taken <span className="text-[#a38e65]">at home</span></SecHead>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <dl className="border-t-2 border-[#1a2942]">
                {examRows.map(([label, value]) => (
                  <div key={label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[120px] sm:shrink-0 font-['Playfair_Display',serif] uppercase tracking-[0.06em] text-[13px] text-[#8f7a56] pt-0.5">{label}</dt>
                    <dd className="flex-1 font-['Playfair_Display',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.8]">{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="lg:pt-1">
                <img
                  src="/images/generated/jba-p-023-qual-master.webp"
                  alt="The highest-level training in leadership and instruction"
                  className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30 mb-4"
                />
                <div className="space-y-5">
                  <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">
                    The Professional Butler certification is required to sit this exam. Take the written exam—including case studies and essay questions—at home, and pass with 80 or higher.
                  </p>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">
                    It evaluates the qualities of a leader, including your philosophy and approach to instruction.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <SecHead roman="VII" en="The Three Tiers">Advance through <span className="text-[#a38e65]">three tiers</span></SecHead>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl mb-8">
              Butler certification is earned step by step—from Associate Butler (Beginner) to Professional Butler (Intermediate) and Master Butler (Advanced). Level-skipping is not permitted. Master Butler stands at the summit as the highest certification.
            </p>
            <ol className="border-t border-[#e2ddd2]">
              {ladder.map((t) => {
                const current = t.key === 'master';
                return (
                  <li key={t.key} className={current ? 'bg-[#1a2942] border-b border-[#e2ddd2]' : 'border-b border-[#e2ddd2]'}>
                    <a href={t.href} className={`flex flex-wrap items-center gap-x-6 gap-y-2 px-5 sm:px-7 py-5 group ${current ? '' : 'hover:bg-[#f8f6f0]'} transition-colors duration-300`}>
                      <span className={`font-['Playfair_Display',serif] text-2xl w-8 shrink-0 ${current ? 'text-[#d7b779]' : 'text-[#a38e65]'}`}>{t.roman}</span>
                      <div className="min-w-[220px]">
                        <span className={`block font-['Playfair_Display',serif] font-medium text-lg ${current ? 'text-[#f8f6f0]' : 'text-[#1a2942]'}`}>{t.name}{current && <span className="ml-3 align-middle text-[10px] tracking-[0.1em] text-[#1a2942] bg-[#d7b779] px-2 py-0.5">CURRENT PAGE</span>}</span>
                        <span className={`block font-['Playfair_Display',serif] italic text-xs mt-0.5 ${current ? 'text-[#c9c2b4]' : 'text-[#8f7a56]'}`}>{t.level}</span>
                      </div>
                      <span className={`font-['Playfair_Display',serif] font-medium text-xl ml-auto ${current ? 'text-[#d7b779]' : 'text-[#1a2942]'}`}>&yen;{t.price}</span>
                      <span className={`font-['Playfair_Display',serif] text-sm shrink-0 ${current ? 'text-[#d7b779]' : 'text-[#a38e65] group-hover:translate-x-1 transition-transform'}`}>{current ? '—' : 'View →'}</span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </section>

          <section>
            <SecHead roman="VIII" en="Enrollment">Enrollment</SecHead>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <div className="flex flex-wrap items-end gap-x-8 gap-y-3 mb-8 pb-8 border-b border-white/15">
                <div>
                  <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#d7b779] mb-1.5">Master Butler · Fee</span>
                  <span className="font-['Playfair_Display',serif] font-medium text-white text-[34px] sm:text-[40px] leading-none">&yen;128,000</span>
                  <span className="block font-['Playfair_Display',serif] text-xs text-white/60 mt-1.5">Tax &amp; shipping included · No certification expiry</span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 flex-1 min-w-[260px]">
                  {['Textbook (approx. 300 pages)', 'Online video lessons (1 year)', 'Practice kit', 'Exam & answer sheet', 'Grading service', 'Certificate & ID card', '3-hour butler salon experience', 'Shipping'].map((x) => (
                    <li key={x} className="flex items-start gap-2 font-['Playfair_Display',serif] text-[13px] text-[#e7e2d8] leading-[1.6]">
                      <span className="text-[#d7b779] text-[11px] pt-1" aria-hidden="true">◆</span>{x}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="font-['Playfair_Display',serif] text-base text-[#e7e2d8] leading-[1.9] max-w-3xl mb-8">
                A special programme for those aiming at the very summit of the profession. As a Master Butler, become the kind of professional who leads the industry forward. Please feel free to contact us with any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Enroll or Ask a Question</a>
                <a href="/en/qualifications" className={BTN08_ON_NAVY}>Back to All Certifications</a>
              </div>
            </div>
            <div className="space-y-5 mt-6">
              <p className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] leading-[1.85] max-w-3xl">
                This is a private certification issued by the Japan Butler Association. The Professional Butler certification is required to apply.
              </p>
              <p className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] leading-[1.85] max-w-3xl">
                It does not guarantee employment, career advancement, or income; it is a learning programme for knowledge acquisition and self-improvement.
              </p>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
